import { Controller } from "@hotwired/stimulus"
import { hideIntercom, showIntercom } from "../helpers/intercom"
import { canSignWithoutConsultant, setSkipConsultantMode, withSkipConsultant } from "../lib/sign_without_consultant"

const SVG_CHECK = '<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>'
const SVG_INFO_CIRCLE = '<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/></svg>'
const SVG_LINK = '<svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/></svg>'

export default class extends Controller {
  static targets = [
    "newCoModal", "modalTitle",
    "pcoSection", "pcoCheckboxTable", "ccdSection", "ccdCheckboxTable", "selectedTotal", "selectedDays",
    "coDropzone", "coFileInput", "coFileList",
    "uploadSection", "uploadLabel",
    "deleteModal", "signModal", "unsignModal", "requestApprovalModal",
    "modalSkipConsultantBtn", "modalConsultantSkipped", "skipConsultantNote", "signTitle", "signConfirmBtn",
    "rejectCoModal", "rejectComment",
    "pastCoConfirmModal", "pastCoSequenceLabel",
    "coHistoryLog", "coHistoryLogEntries",
    "saveDraftBtn", "signRequestBtn",
    "modalRequestApprovalBtn", "modalSignedBadge", "modalSignedText", "modalSignedTooltip", "modalSignedCheck",
    "modalPendingText",
    "unsignBtn", "modalDeleteBtn", "modalEditPastBtn", "modalApproveBtn", "modalRejectBtn", "addPastCoBtn",
    "overallTotals", "overallAmount", "overallDays", "coCards", "emptyState"
  ]

  static values = {
    projectId: Number,
    currency: String,
    currencyFormat: String,
    delimiter: String,
    separator: String,
    isOwner: Boolean,
    // PM Permissions: stakeholder granted owner-side CO approval
    // (approve/reject side only — no editing or past-CO powers).
    canApprove: Boolean,
    // Approval Settings → "Allow Owners To Sign Without The Consultant".
    skipConsultantAllowed: Boolean,
    isConsultant: Boolean,
    isContractor: Boolean,
    sovApproved: Boolean,
    hasConsultant: Boolean
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._editSequence = null
    this._isPast = false
    this._attachmentIds = []
    this._pendingUploads = 0
    this._pendingSignSequence = null
    this._signFromEditModal = false
    this._signFromEditIsNew = false
    this._approveAfterAcknowledge = false
    this._editHasCcds = false
    this._editContractorSigned = false
    this._editConsultantSigned = false
    this._requiredSigs = this.hasConsultantValue ? 3 : 2
    this._pdfPollingTimers = {}
  }

  disconnect() {
    Object.values(this._pdfPollingTimers).forEach(t => clearTimeout(t))
    this._pdfPollingTimers = {}
  }

  // -- Currency helpers --

  _parseCurrency(str) {
    if (!str) return 0
    let cleaned = str.replace(this.currencyValue, "").trim()
    let neg = false
    if (cleaned.startsWith("(") && cleaned.endsWith(")")) {
      neg = true
      cleaned = cleaned.slice(1, -1)
    }
    if (this.currencyFormatValue === "european_currency_format") {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      cleaned = cleaned.replace(/'/g, "")
    } else {
      cleaned = cleaned.replace(/,/g, "")
    }
    const val = parseFloat(cleaned) || 0
    return neg ? -val : val
  }

  _formatAmount(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    let formatted
    if (this.currencyFormatValue === "european_currency_format") {
      formatted = abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
    if (num < 0) return `(${formatted})`
    return formatted
  }

  _escAttr(str) {
    return String(str).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  // -- New CO --

  async newCo() {
    if (!this.sovApprovedValue) {
      this._showToast("Schedule of values must be approved before creating a CO.", "alert")
      return
    }
    this._editSequence = null
    this._isPast = false
    this.modalTitleTarget.textContent = "New Change Order"
    await this._loadModalData()
  }

  async newPastCo() {
    if (!this.sovApprovedValue) {
      this._showToast("Schedule of values must be approved before creating a CO.", "alert")
      return
    }
    this._editSequence = null
    this._isPast = true
    this.modalTitleTarget.textContent = "Past Change Order"
    await this._loadModalData()
  }

  async editCo(event) {
    const sequence = event.currentTarget.dataset.sequence
    this._editSequence = sequence
    const card = this.element.querySelector(`.co-card[data-sequence="${sequence}"]`)
    this._isPast = card?.dataset.isPast === "true"
    this.modalTitleTarget.textContent = this._isPast ? `Past CO #${sequence}` : `Edit CO #${sequence}`
    await this._loadModalData(sequence)
  }

  async _loadModalData(sequence = null) {
    const url = `/projects/${this.projectIdValue}/finances/new_change_order_modal_data${sequence ? `?sequence=${sequence}` : ""}`
    const resp = await fetch(url, { headers: { Accept: "application/json" } })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      this._showToast(err.error || "An error occurred.", "alert")
      return
    }
    const data = await resp.json()

    this._requiredSigs = data.required_sigs || (this.hasConsultantValue ? 3 : 2)
    this._editHasCcds = data.has_ccds || false
    this._editContractorSigned = data.contractor_signed || false
    this._editConsultantSigned = data.consultant_signed || false
    this._attachmentIds = []
    this._pendingUploads = 0

    if (this.hasCoFileListTarget) this.coFileListTarget.innerHTML = ""

    // Populate PCO checkbox table
    const signedCount = (data.contractor_signed ? 1 : 0) + (data.consultant_signed ? 1 : 0) + (data.owner_signed ? 1 : 0)
    const isOwnerPast = this.isOwnerValue && (this._isPast || !!data.is_past)
    const canEdit = this.isContractorValue || this.isConsultantValue || isOwnerPast
    const checkboxesReadOnly = !canEdit || (!isOwnerPast && (signedCount > 0 || !!data.is_past))
    this._renderPcoTable(data.available_pcos || [], data.selected_pco_sequences || [], checkboxesReadOnly, data.available_ccds || [], data.selected_ccd_sequences || [])

    // Load existing attachments
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach(att => {
        this._attachmentIds.push(att.id)
        const chip = this._createFileChip(att.filename, {
          removable: true,
          url: att.url,
          onRemove: () => {
            chip.remove()
            this._attachmentIds = this._attachmentIds.filter(id => id !== att.id)
          }
        })
        if (this.hasCoFileListTarget) this.coFileListTarget.appendChild(chip)
      })
    }

    this._renderHistoryLog(data.history_log || [])
    this._renderModalHeaderBadges(data)
    this._recalcSelectedTotals()

    this.newCoModalTarget.classList.remove("hidden")
    hideIntercom()
  }

  _renderPcoTable(pcos, selectedSequences, readOnly = false, ccds = [], selectedCcdSequences = []) {
    const cur = this.currencyValue
    const isEditing = !!this._editSequence

    // PCO section
    const pcoTbody = this.pcoCheckboxTableTarget
    pcoTbody.innerHTML = ""
    if (pcos.length > 0) {
      this.pcoSectionTarget.classList.remove("hidden")
      pcos.forEach(pco => {
        const isSelected = selectedSequences.includes(pco.sequence)
        const isLinkedToOther = pco.linked_co && (!isEditing || pco.linked_co !== parseInt(this._editSequence))
        const disabled = isLinkedToOther

        const tr = document.createElement("tr")
        tr.className = (disabled || readOnly) ? "bg-gray-50 text-gray-400" : "hover:bg-gray-50"
        const lastUpdated = pco.last_updated_at && pco.who_updated
          ? `<span class="text-gray-400 text-[12px] italic ml-2">Last updated on ${this._escAttr(pco.last_updated_at)} by ${this._escAttr(pco.who_updated)}</span>`
          : ""

        const cbClass = disabled
          ? "rounded border-gray-300 text-brand focus:ring-brand opacity-50"
          : readOnly
            ? "rounded border-blue-300 text-brand focus:ring-brand opacity-70 pointer-events-none"
            : "rounded border-gray-300 text-brand focus:ring-brand"

        tr.innerHTML = `
          <td class="px-2 py-1.5 text-center">
            <input type="checkbox" data-pco-sequence="${pco.sequence}" ${isSelected ? "checked" : ""} ${disabled ? "disabled" : ""}
                   data-action="change->change-order#togglePco"
                   class="${cbClass}">
          </td>
          <td class="px-2 py-1.5 font-medium">PCO #${pco.sequence}</td>
          <td class="px-2 py-1.5 truncate max-w-[200px]">${this._escAttr(pco.title || "")}</td>
          <td class="px-2 py-1.5 text-right">${cur}${this._formatAmount(pco.total || 0)}</td>
          <td class="px-2 py-1.5 text-right">${pco.days || 0}</td>
          <td class="px-2 py-1.5">
            ${isLinkedToOther ? `<span class="inline-flex items-center gap-1 bg-blue-50 text-blue-600 text-[11px] font-medium rounded px-1.5 py-[1px]">${SVG_LINK} Added to CO#${pco.linked_co}</span>` : ""}
            ${pco.is_past ? `<span class="inline-flex items-center gap-1 bg-gray-100 text-gray-500 text-[11px] font-medium rounded px-1.5 py-[1px]">Past PCO</span>` : pco.signed_count >= pco.required_sigs ? `<span class="inline-flex items-center gap-1 bg-green-50 text-green-600 text-[11px] font-medium rounded px-1.5 py-[1px]">${pco.signed_count}/${pco.required_sigs} Signed</span>` : ""}
            ${lastUpdated}
          </td>
        `
        pcoTbody.appendChild(tr)
      })
    } else {
      this.pcoSectionTarget.classList.add("hidden")
    }

    // CCD section
    const ccdTbody = this.ccdCheckboxTableTarget
    ccdTbody.innerHTML = ""
    if (ccds.length > 0) {
      this.ccdSectionTarget.classList.remove("hidden")
      ccds.forEach(ccd => {
        const isSelected = selectedCcdSequences.includes(ccd.sequence)
        const isLinkedToOther = ccd.linked_co && (!isEditing || ccd.linked_co !== parseInt(this._editSequence))
        const disabled = isLinkedToOther

        const tr = document.createElement("tr")
        tr.className = (disabled || readOnly) ? "bg-gray-50 text-gray-400" : "hover:bg-gray-50"

        const cbClass = disabled
          ? "rounded border-gray-300 text-brand focus:ring-brand opacity-50"
          : readOnly
            ? "rounded border-blue-300 text-brand focus:ring-brand opacity-70 pointer-events-none"
            : "rounded border-gray-300 text-brand focus:ring-brand"

        tr.innerHTML = `
          <td class="px-2 py-1.5 text-center">
            <input type="checkbox" data-ccd-sequence="${ccd.sequence}" ${isSelected ? "checked" : ""} ${disabled ? "disabled" : ""}
                   data-action="change->change-order#togglePco"
                   class="${cbClass}">
          </td>
          <td class="px-2 py-1.5 font-medium">CCD #${ccd.sequence}</td>
          <td class="px-2 py-1.5 truncate max-w-[200px]">${this._escAttr(ccd.title || "")}</td>
          <td class="px-2 py-1.5 text-right">${cur}${this._formatAmount(ccd.total || 0)}</td>
          <td class="px-2 py-1.5 text-right">${ccd.days || 0}</td>
          <td class="px-2 py-1.5">
            ${isLinkedToOther ? `<span class="inline-flex items-center gap-1 bg-blue-50 text-blue-600 text-[11px] font-medium rounded px-1.5 py-[1px]">${SVG_LINK} Added to CO#${ccd.linked_co}</span>` : ""}
            <span class="inline-flex items-center gap-1 bg-green-50 text-green-600 text-[11px] font-medium rounded px-1.5 py-[1px]">Issued</span>
          </td>
        `
        ccdTbody.appendChild(tr)
      })
    } else {
      this.ccdSectionTarget.classList.add("hidden")
    }
  }

  togglePco() {
    this._recalcSelectedTotals()
  }

  _recalcSelectedTotals() {
    let total = 0
    let days = 0
    const tables = [this.pcoCheckboxTableTarget, this.ccdCheckboxTableTarget]
    tables.forEach(tbody => {
      tbody.querySelectorAll("input[type='checkbox']:checked").forEach(cb => {
        const row = cb.closest("tr")
        const amountCell = row.querySelectorAll("td")[3]
        const daysCell = row.querySelectorAll("td")[4]
        total += this._parseCurrency(amountCell?.textContent)
        let daysText = (daysCell?.textContent || "").trim()
        let daysVal = 0
        if (daysText.startsWith("(") && daysText.endsWith(")")) {
          daysVal = -(parseInt(daysText.slice(1, -1)) || 0)
        } else {
          daysVal = parseInt(daysText) || 0
        }
        days += daysVal
      })
    })
    if (this.hasSelectedTotalTarget) {
      this.selectedTotalTarget.textContent = `${this.currencyValue}${this._formatAmount(total)}`
    }
    if (this.hasSelectedDaysTarget) {
      this.selectedDaysTarget.textContent = days
    }
  }

  _getSelectedPcoSequences() {
    const sequences = []
    this.pcoCheckboxTableTarget.querySelectorAll("input[type='checkbox'][data-pco-sequence]:checked").forEach(cb => {
      sequences.push(parseInt(cb.dataset.pcoSequence))
    })
    return sequences
  }

  _getSelectedCcdSequences() {
    const sequences = []
    this.ccdCheckboxTableTarget.querySelectorAll("input[type='checkbox'][data-ccd-sequence]:checked").forEach(cb => {
      sequences.push(parseInt(cb.dataset.ccdSequence))
    })
    return sequences
  }

  // -- Save / Create --

  async saveDraft() { await this._submitCo(false) }
  async signAndRequest() {
    if (this.isConsultantValue && this._editSequence) {
      // Editing existing CO — consultants just sign directly
      if (this._hasPreviousUnsignedCos(this._editSequence)) {
        this._showToast("All previous Change Orders must be fully signed before signing a new one.", "alert")
        return
      }
      this._pendingSignSequence = this._editSequence
      this._signFromEditModal = true
      this._signFromEditIsNew = false
      this.signModalTarget.classList.remove("hidden")
      return
    }
    await this._submitCo(true)
  }

  async _submitCo(signAfter) {
    if (this._submitting) return
    this._submitting = true
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = true
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = true

    try {
    const selectedPcos = this._getSelectedPcoSequences()
    const selectedCcds = this._getSelectedCcdSequences()
    if (selectedPcos.length === 0 && selectedCcds.length === 0) {
      this._showToast("Please select at least one PCO or CCD.", "alert")
      return
    }

    const sequence = this._editSequence || this._getNextCoSequence()
    const isUpdate = !!this._editSequence

    let action
    if (isUpdate) {
      action = "update_change_order_final"
    } else {
      action = this._isPast ? "create_past_change_order_final" : "create_change_order_final"
    }

    const method = isUpdate ? "PUT" : "POST"
    const params = {
      sequence: sequence,
      pco_sequences: JSON.stringify(selectedPcos),
      ccd_sequences: JSON.stringify(selectedCcds),
      attachment_ids: this._attachmentIds.join(" ")
    }

    const saveResp = await this._ajax(method, action, params)

    if (signAfter && !this._isPast) {
      if (this._hasPreviousUnsignedCos(sequence)) {
        this._showToast("All previous Change Orders must be fully signed before signing a new one.", "alert")
        return
      }
      this._pendingSignSequence = sequence
      this._signFromEditModal = true
      this._signFromEditIsNew = !isUpdate
      this.signModalTarget.classList.remove("hidden")
      return
    }

    if (saveResp) {
      if (isUpdate) {
        this._updatePageCard(saveResp)
      } else {
        this._insertNewPageCard(saveResp)
      }
      this._updateOverallTotals(saveResp.overall_total, saveResp.overall_days)
    }

    this.hideModal()
    this._showToast("Saved", "success")
    this._startPdfPolling(sequence, "CO")
    } finally {
      this._submitting = false
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = false
      if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = false
    }
  }

  _getNextCoSequence() {
    const cards = this.element.querySelectorAll(".co-card[data-sequence]")
    let max = 0
    cards.forEach(el => {
      const seq = parseInt(el.dataset.sequence)
      if (seq > max) max = seq
    })
    return max + 1
  }

  // -- Past CO --

  async addPastCo() {
    const selectedPcos = this._getSelectedPcoSequences()
    const selectedCcds = this._getSelectedCcdSequences()
    if (selectedPcos.length === 0 && selectedCcds.length === 0) {
      this._showToast("Please select at least one PCO or CCD.", "alert")
      return
    }
    const nextSequence = this._getNextCoSequence()
    if (this.hasPastCoSequenceLabelTarget) {
      this.pastCoSequenceLabelTarget.textContent = `CO#${nextSequence}`
    }
    this.pastCoConfirmModalTarget.classList.remove("hidden")
  }

  async confirmPastCo() {
    this.pastCoConfirmModalTarget.classList.add("hidden")
    this._isPast = true
    await this._submitCo(false)
  }

  hidePastCoConfirmModal() {
    this.pastCoConfirmModalTarget.classList.add("hidden")
  }

  // -- Edit Past CO --

  editPastCo() {
    // Unlock the modal for editing
    this._setModalReadonly(false)
    if (this.hasModalEditPastBtnTarget) this.modalEditPastBtnTarget.classList.add("hidden")
    if (this.hasSaveDraftBtnTarget) {
      this.saveDraftBtnTarget.classList.remove("hidden")
      this.saveDraftBtnTarget.textContent = "Update Past CO"
    }
  }

  // -- Delete --

  deleteFromModal() {
    const cards = this.element.querySelectorAll(".co-card[data-sequence]")
    let maxSeq = 0
    cards.forEach(el => { const s = parseInt(el.dataset.sequence); if (s > maxSeq) maxSeq = s })
    if (parseInt(this._editSequence) !== maxSeq) {
      this._showToast("Must delete the most recent item first. To delete this item, please first delete any more recent records.", "alert")
      return
    }
    this._pendingDeleteSequence = this._editSequence
    this.deleteModalTarget.classList.remove("hidden")
  }

  hideDeleteModal() { this.deleteModalTarget.classList.add("hidden") }

  async confirmDelete() {
    const resp = await this._ajax("DELETE", "destroy_change_order_final", { sequence: this._pendingDeleteSequence })
    this.hideDeleteModal()
    this.hideModal()
    this._removePageCard(this._pendingDeleteSequence)

    if (resp && resp.has_any_cos) {
      this._updateOverallTotals(resp.overall_total, resp.overall_days)
    } else {
      if (this.hasOverallTotalsTarget) this.overallTotalsTarget.classList.add("hidden")
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    }
    this._showToast("Deleted", "success")
  }

  // -- Sign / Unsign --

  showSignModal(sequence) {
    this._pendingSignSequence = sequence
    this.signModalTarget.classList.remove("hidden")
  }

  hideSignModal() {
    setSkipConsultantMode(this, false)
    if (this._signFromEditModal || this._approveAfterAcknowledge) {
      this.signModalTarget.classList.add("hidden")
      this._signFromEditModal = false
      this._signFromEditIsNew = false
      this._approveAfterAcknowledge = false
      return
    }
    this.signModalTarget.classList.add("hidden")
  }

  async confirmSign(event) {
    // One request per gesture — key-repeat (held Enter) or double-clicks fire
    // this repeatedly while the request is still in flight.
    if (this._confirmInFlight) return
    this._confirmInFlight = true
    const confirmBtn = event?.currentTarget
    if (confirmBtn) confirmBtn.disabled = true
    try {
      await this._confirmSignInner()
    } finally {
      this._confirmInFlight = false
      if (confirmBtn) confirmBtn.disabled = false
    }
  }

  async _confirmSignInner() {
    const fromEditModal = this._signFromEditModal
    const isNew = this._signFromEditIsNew
    const fromApprove = this._approveAfterAcknowledge
    this._signFromEditModal = false
    this._signFromEditIsNew = false
    this._approveAfterAcknowledge = false

    const resp = await this._ajax("POST", "sign_co", withSkipConsultant(this, { sequence: this._pendingSignSequence }))
    this.signModalTarget.classList.add("hidden")
    if (resp) {
      this._editHasCcds = resp.has_ccds || false
      this._editContractorSigned = resp.contractor_signed || false
      this._editConsultantSigned = resp.consultant_signed || false
    }

    if (fromApprove) {
      if (resp) {
        this._updatePageCard(resp)
        this._updateModalState(resp)
      }
      this.hideModal()
      this._showToast("Signed", "success")
      this._startPdfPolling(this._pendingSignSequence, "CO")
      return
    }

    if (fromEditModal) {
      if (resp) {
        if (isNew) {
          this._insertNewPageCard(resp)
        } else {
          this._updatePageCard(resp)
        }
        this._updateOverallTotals(resp.overall_total, resp.overall_days)
        this._updateModalState(resp)
      }

      if (this.isContractorValue || this.isConsultantValue) {
        this._signedBeforeRequest = true
        const hasCcds = resp ? (resp.has_ccds || false) : this._editHasCcds
        const consultantSection = this.requestApprovalModalTarget.querySelector('[data-company-section="consultant"]')
        const contractorSection = this.requestApprovalModalTarget.querySelector('[data-company-section="contractor"]')

        if (hasCcds) {
          // CCD-based CO: all parties must sign deliberately.
          // Show sections for unsigned roles only (hide own role and already-signed roles).
          const consultantAlreadySigned = resp ? resp.consultant_signed : this._editConsultantSigned
          const contractorAlreadySigned = resp ? resp.contractor_signed : this._editContractorSigned
          if (consultantSection) consultantSection.classList.toggle("hidden", this.isConsultantValue || !this.hasConsultantValue || consultantAlreadySigned)
          if (contractorSection) contractorSection.classList.toggle("hidden", this.isContractorValue || contractorAlreadySigned)
        } else {
          // PCO-only CO: other party's signature was auto-imported, only need owner approval.
          if (consultantSection) consultantSection.classList.add("hidden")
          if (contractorSection) contractorSection.classList.add("hidden")
        }

        this.requestApprovalModalTarget.classList.remove("hidden")
      } else {
        this.hideModal()
      }
      this._showToast("Signed", "success")
      this._startPdfPolling(this._pendingSignSequence, "CO")
      return
    }

    if (resp) this._updatePageCard(resp)
    this._showToast("Signed", "success")
    this._startPdfPolling(this._pendingSignSequence, "CO")
  }

  unsignFromModal() {
    this._pendingSignSequence = this._editSequence
    this.unsignModalTarget.classList.remove("hidden")
  }

  hideUnsignModal() { this.unsignModalTarget.classList.add("hidden") }

  async confirmUnsign() {
    const resp = await this._ajax("POST", "unsign_co", { sequence: this._pendingSignSequence })
    this.hideUnsignModal()
    if (resp) {
      this._updatePageCard(resp)
      this._updateModalState(resp)
    }
    this._showToast("Signature removed", "success")
    this._startPdfPolling(this._pendingSignSequence, "CO")
  }

  // -- Approve / Reject --

  // The owner's Sign & Approve pop-up, in "without the consultant" mode.
  signWithoutConsultant() {
    this.approveCo()
    setSkipConsultantMode(this, true)
  }

  approveCo() {
    this._pendingSignSequence = this._editSequence
    this._approveAfterAcknowledge = true
    this.signModalTarget.classList.remove("hidden")
  }

  showRejectCoModal() { this.rejectCoModalTarget.classList.remove("hidden") }
  hideRejectCoModal() {
    this.rejectCoModalTarget.classList.add("hidden")
    if (this.hasRejectCommentTarget) this.rejectCommentTarget.value = ""
  }

  async confirmRejectCo() {
    const comment = this.hasRejectCommentTarget ? this.rejectCommentTarget.value : ""
    const resp = await this._ajax("POST", "reject_co", { sequence: this._editSequence, comment })
    this.hideRejectCoModal()
    if (resp) {
      this._updatePageCard(resp)
      this._updateModalState(resp)
    }
    this.hideModal()
    this._showToast("Rejected", "success")
  }

  // -- Request Approval --

  showRequestApprovalModal() {
    // When manually showing the approval modal, set section visibility based on CCD status
    const consultantSection = this.requestApprovalModalTarget.querySelector('[data-company-section="consultant"]')
    const contractorSection = this.requestApprovalModalTarget.querySelector('[data-company-section="contractor"]')

    if (this._editHasCcds) {
      // CCD-based CO: show sections for unsigned roles only (hide own role and already-signed roles)
      if (consultantSection) consultantSection.classList.toggle("hidden", this.isConsultantValue || !this.hasConsultantValue || this._editConsultantSigned)
      if (contractorSection) contractorSection.classList.toggle("hidden", this.isContractorValue || this._editContractorSigned)
    } else {
      // PCO-only CO: other party's sig was auto-imported, only need owner
      if (consultantSection) consultantSection.classList.add("hidden")
      if (contractorSection) contractorSection.classList.add("hidden")
    }

    this.requestApprovalModalTarget.classList.remove("hidden")
  }
  hideRequestApprovalModal() {
    this.requestApprovalModalTarget.classList.add("hidden")
    if (this._signedBeforeRequest) {
      this._signedBeforeRequest = false
      this.hideModal()
    }
  }

  async sendApprovalRequest() {
    const sections = this.requestApprovalModalTarget.querySelectorAll("[data-company-section]")
    for (const section of sections) {
      if (section.classList.contains("hidden")) continue
      const checked = section.querySelectorAll("input[name='approval_email']:checked")
      if (checked.length === 0) {
        this._showToast("Please select at least one person from each company.", "alert")
        return
      }
    }

    const checkboxes = this.requestApprovalModalTarget.querySelectorAll("input[name='approval_email']:checked")
    const emails = Array.from(checkboxes).map(cb => cb.dataset.email)
    if (emails.length === 0) return

    const resp = await this._ajax("POST", "request_co_approval", {
      sequence: this._pendingSignSequence,
      emails: JSON.stringify(emails)
    })
    if (resp) this._updatePageCard(resp)
    this._signedBeforeRequest = false
    this.requestApprovalModalTarget.classList.add("hidden")
    this.hideModal()
    this._showToast("Approval request sent", "success")
  }

  // -- File upload --

  dragoverDropzone(event) {
    event.preventDefault()
    this.coDropzoneTarget.classList.add("border-brand", "bg-blue-50/50")
  }

  dragleaveDropzone() {
    this.coDropzoneTarget.classList.remove("border-brand", "bg-blue-50/50")
  }

  dropDropzone(event) {
    event.preventDefault()
    this.coDropzoneTarget.classList.remove("border-brand", "bg-blue-50/50")
    const files = Array.from(event.dataTransfer.files).filter(f =>
      f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    )
    if (files.length > 0) this._doFileUpload(files)
  }

  clickDropzone() {
    this.coFileInputTarget.click()
  }

  selectFiles(event) {
    const files = Array.from(event.target.files)
    if (files.length > 0) this._doFileUpload(files)
    event.target.value = ""
  }

  async _doFileUpload(files) {
    const token = this._csrfToken
    this._pendingUploads++
    this._updateUploadState()

    const placeholders = files.map(file => {
      const chip = this._createFileChip(file.name, { uploading: true })
      this.coFileListTarget.appendChild(chip)
      return chip
    })

    try {
      const presignUrl = `/projects/${this.projectIdValue}/finances/presign_uploads`
      const presignRes = await fetch(presignUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: files.map(f => ({
            filename: f.name,
            content_type: f.type || "application/pdf",
            byte_size: f.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }

      const { uploads } = await presignRes.json()

      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": files[i].type || "application/pdf" },
            body: files[i]
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return upload
        })
      )

      const successful = results.filter(r => r.status === "fulfilled").map(r => r.value)
      if (successful.length === 0) throw new Error("All uploads failed")

      const registerUrl = `/projects/${this.projectIdValue}/finances/register_uploads`
      const registerRes = await fetch(registerUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(u => ({ cache_name: u.cache_name, filename: u.filename }))
        })
      })

      const registered = await registerRes.json()

      placeholders.forEach(p => p.remove())
      registered.forEach(a => {
        const name = a.filename || `File #${a.id}`
        this._attachmentIds.push(a.id)
        const chip = this._createFileChip(name, {
          removable: true,
          onRemove: () => {
            chip.remove()
            this._attachmentIds = this._attachmentIds.filter(id => id !== a.id)
          }
        })
        this.coFileListTarget.appendChild(chip)
      })
    } catch (e) {
      console.error("File upload failed:", e)
      placeholders.forEach(p => p.remove())
      this._showToast("File upload failed", "alert")
    } finally {
      this._pendingUploads--
      this._updateUploadState()
    }
  }

  _updateUploadState() {
    const uploading = this._pendingUploads > 0
    if (this.hasSaveDraftBtnTarget) this._setButtonUploading(this.saveDraftBtnTarget, uploading)
    if (this.hasSignRequestBtnTarget) this._setButtonUploading(this.signRequestBtnTarget, uploading)
    if (this.hasAddPastCoBtnTarget) this._setButtonUploading(this.addPastCoBtnTarget, uploading)
  }

  _setButtonUploading(btn, uploading) {
    btn.disabled = uploading
    btn.classList.toggle("opacity-60", uploading)
    btn.classList.toggle("cursor-not-allowed", uploading)
    btn.classList.toggle("pointer-events-none", uploading)
    if (uploading) {
      if (!btn.dataset.originalText) {
        btn.dataset.originalText = btn.textContent
        btn.style.minWidth = `${btn.offsetWidth}px`
      }
      btn.innerHTML = `<svg class="animate-spin h-4 w-4 inline-block align-middle mr-1 -mt-px" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>Uploading\u2026`
    } else if (btn.dataset.originalText) {
      btn.textContent = btn.dataset.originalText
      delete btn.dataset.originalText
      btn.style.minWidth = ""
    }
  }

  _createFileChip(name, { removable = false, uploading = false, onRemove = null, url = null } = {}) {
    const chip = document.createElement(url ? "a" : "div")
    if (url) {
      chip.href = url
      chip.target = "_blank"
      chip.rel = "noopener"
    }
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? "pr-1" : "pr-2"} py-1 rounded-md text-xs${url ? " cursor-pointer hover:opacity-80" : ""}`
    chip.classList.add(uploading ? "chip-uploading" : "chip-file")

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0 chip-file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${this._escAttr(name)}">${truncatedName}</span>${removeBtn}`

    if (removable && onRemove) {
      const btn = chip.querySelector("[data-remove-chip]")
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        onRemove()
      })
    }

    return chip
  }

  // -- History Log --

  _renderHistoryLog(entries) {
    if (!this.hasCoHistoryLogTarget) return
    this.coHistoryLogEntriesTarget.innerHTML = ""
    if (entries.length === 0) {
      this.coHistoryLogTarget.classList.add("hidden")
      return
    }
    entries.forEach(entry => {
      const p = document.createElement("p")
      p.className = "text-[12px] text-gray-400 leading-5"
      p.textContent = `${entry.datetime} - ${entry.username} (${entry.company_role}) ${entry.message}`
      this.coHistoryLogEntriesTarget.appendChild(p)
    })
    this.coHistoryLogTarget.classList.remove("hidden")
  }

  // -- Modal header badges --

  _renderModalHeaderBadges(data) {
    const isEditing = !!this._editSequence
    const signedCount = data.signed_count || 0
    const tooltip = data.signature_tooltip || ""
    const showRequestBtn = data.show_request_approval_btn || false
    const isPast = data.is_past_payment || false
    const myRoleSigned = isEditing && data.my_role_signed
    const reqSigs = this._requiredSigs

    // Request Approval button
    if (this.hasModalRequestApprovalBtnTarget) {
      if (isEditing && showRequestBtn && signedCount > 0) {
        this._pendingSignSequence = this._editSequence
        this.modalRequestApprovalBtnTarget.classList.remove("hidden")
        this.modalRequestApprovalBtnTarget.classList.add("inline-flex")
      } else {
        this.modalRequestApprovalBtnTarget.classList.add("hidden")
        this.modalRequestApprovalBtnTarget.classList.remove("inline-flex")
      }
    }

    // Signed badge
    if (this.hasModalSignedBadgeTarget) {
      if (isEditing && (signedCount > 0 || isPast)) {
        if (isPast) {
          this.modalSignedTextTarget.textContent = "Past CO"
        } else {
          this.modalSignedTextTarget.textContent = `${signedCount}/${reqSigs} Signed`
        }
        this.modalSignedCheckTarget.classList.add("hidden")
        if (!isPast && tooltip) {
          this.modalSignedTooltipTarget.setAttribute("data-tip", tooltip)
          this.modalSignedTooltipTarget.setAttribute("data-tip-align", "left")
          this.modalSignedTooltipTarget.setAttribute("data-tip-pos", "below")
          this.modalSignedTooltipTarget.classList.remove("hidden")
        } else {
          this.modalSignedTooltipTarget.classList.add("hidden")
        }
        if (!isPast && myRoleSigned) this.modalSignedCheckTarget.classList.remove("hidden")
        const badge = this.modalSignedBadgeTarget
        badge.classList.remove("bg-green-100", "text-green-600", "bg-amber-100", "text-amber-600", "bg-gray-200", "text-gray-600")
        if (isPast) {
          badge.classList.add("bg-gray-200", "text-gray-600")
        } else if (signedCount < reqSigs) {
          badge.classList.add("bg-amber-100", "text-amber-600")
        } else {
          badge.classList.add("bg-green-100", "text-green-600")
        }
        badge.classList.remove("hidden")
        badge.classList.add("inline-flex")
      } else {
        this.modalSignedBadgeTarget.classList.add("hidden")
        this.modalSignedBadgeTarget.classList.remove("inline-flex")
      }
    }

    // Delete button
    if (this.hasModalDeleteBtnTarget) {
      const showDelete = isEditing && (((this.isContractorValue || this.isConsultantValue) && signedCount === 0 && !isPast) || (this.isOwnerValue && isPast))
      this.modalDeleteBtnTarget.classList.toggle("hidden", !showDelete)
    }

    // Edit Past CO button — no longer needed, owners have direct edit access
    if (this.hasModalEditPastBtnTarget) {
      this.modalEditPastBtnTarget.classList.add("hidden")
    }

    // Pending text
    const contractorSigned = data.contractor_signed || false
    const consultantSigned = data.consultant_signed || false
    if (this.hasModalPendingTextTarget) {
      let pendingMsg = ""
      if (isEditing && !isPast) {
        if (signedCount === 0) {
          pendingMsg = "Pending Contractor Signature"
        } else if (contractorSigned && !consultantSigned && this._requiredSigs >= 3) {
          pendingMsg = "Pending Consultant Signature"
        } else if (consultantSigned && !contractorSigned) {
          pendingMsg = "Pending Contractor Signature"
        }
      }
      this.modalPendingTextTarget.textContent = pendingMsg
      this.modalPendingTextTarget.classList.toggle("hidden", !pendingMsg)
    }

    // Approve
    // ownerApprover = owner, or a stakeholder granted CO approval (PM Permissions)
    const ownerApprover = this.isOwnerValue || this.canApproveValue
    // An owner who signed without the consultant: say who.
    const skippedBy = isEditing ? data.consultant_skipped_by : null
    if (this.hasModalConsultantSkippedTarget) {
      const label = this.modalConsultantSkippedTarget
      label.textContent = skippedBy ? label.dataset.template.replace("%{who}", skippedBy) : ""
      label.classList.toggle("hidden", !skippedBy)
    }

    // "Sign without consultant" — owner side, contractor signed, consultant not yet
    if (this.hasModalSkipConsultantBtnTarget) {
      this.modalSkipConsultantBtnTarget.classList.toggle("hidden", !(isEditing && !isPast && canSignWithoutConsultant({
        allowed: this.skipConsultantAllowedValue, ownerApprover, myRoleSigned,
        contractorSigned, consultantSigned, requiredSigs: this._requiredSigs
      })))
    }

    const canConsultantApprove = this.isConsultantValue && contractorSigned && !consultantSigned
    const canOwnerApprove = ownerApprover && contractorSigned && (consultantSigned || this._requiredSigs <= 2) && !myRoleSigned
    const showApprove = canOwnerApprove && isEditing && !isPast
    if (this.hasModalApproveBtnTarget) this.modalApproveBtnTarget.classList.toggle("hidden", !showApprove)

    // Reject
    const showReject = (ownerApprover || this.isConsultantValue) && isEditing && signedCount >= 1 && !isPast
    if (this.hasModalRejectBtnTarget) this.modalRejectBtnTarget.classList.toggle("hidden", !showReject)

    // Sign & Request — contractor (when unsigned) or consultant (when unsigned)
    const canContractorSign = this.isContractorValue && !contractorSigned
    const canConsultantSign = this.isConsultantValue && !consultantSigned && !skippedBy
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.classList.toggle("hidden", !(canContractorSign || canConsultantSign) || isPast)
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.classList.toggle("hidden", myRoleSigned || !(this.isContractorValue || this.isConsultantValue) || signedCount > 0 || isPast)

    // Unsign
    const showUnsign = isEditing && myRoleSigned && !isPast && signedCount < this._requiredSigs
    if (this.hasUnsignBtnTarget) this.unsignBtnTarget.classList.toggle("hidden", !showUnsign)

    // Add Past CO — owner creating new past CO
    if (this.hasAddPastCoBtnTarget) this.addPastCoBtnTarget.classList.toggle("hidden", !(this._isPast && this.isOwnerValue && !isEditing))

    // Lock fields when signed, past (except owner editing past CO), or non-creator viewing
    const ownerEditingPast = this.isOwnerValue && isPast
    const locked = myRoleSigned || (isPast && !ownerEditingPast) || (!this.isContractorValue && !this.isConsultantValue && !ownerEditingPast && isEditing && signedCount > 0)
    this._setModalReadonly(locked)
  }

  _setModalReadonly(locked) {
    this.pcoCheckboxTableTarget.querySelectorAll("input[type='checkbox']").forEach(cb => {
      if (!cb.disabled) cb.disabled = locked
    })
    const hideDropzone = locked || !(this.isContractorValue || this.isConsultantValue || (this.isOwnerValue && this._isPast))
    if (this.hasCoDropzoneTarget) this.coDropzoneTarget.classList.toggle("hidden", hideDropzone)
    if (this.hasUploadLabelTarget) this.uploadLabelTarget.classList.toggle("hidden", hideDropzone)
    if (this.hasCoFileListTarget) {
      this.coFileListTarget.querySelectorAll("[data-remove-chip]").forEach(btn => {
        btn.classList.toggle("hidden", hideDropzone)
      })
    }
  }

  _updateModalState(data) {
    this._renderModalHeaderBadges(data)
    this._renderHistoryLog(data.history_log || [])
  }

  // -- Page card DOM helpers --

  _updateOverallTotals(overallTotal, overallDays) {
    if (this.hasOverallAmountTarget) {
      this.overallAmountTarget.innerHTML = `<b>${this._formatAmount(overallTotal)}</b>`
    }
    if (this.hasOverallDaysTarget) {
      this.overallDaysTarget.textContent = overallDays
    }
    if (this.hasOverallTotalsTarget) {
      this.overallTotalsTarget.classList.remove("hidden")
    }
  }

  _buildBadgeHTML(data) {
    const signedCount = data.signed_count || 0
    const isPast = data.is_past_payment || false
    const tooltip = data.signature_tooltip || ""
    const myRoleSigned = data.my_role_signed || false
    const reqSigs = data.required_sigs || this._requiredSigs

    let html = ""

    if (isPast) {
      html += `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">Past CO</span>`
    } else if (signedCount > 0) {
      const badgeBg = signedCount < reqSigs ? "bg-amber-100 text-amber-600" : "bg-green-100 text-green-600"
      let iconHtml = ""
      if (signedCount >= reqSigs && myRoleSigned) iconHtml = SVG_CHECK
      let tooltipHtml = ""
      if (tooltip) tooltipHtml = `<span class="shrink-0 cursor-help flex items-center" data-tip="${this._escAttr(tooltip)}" data-tip-align="left" data-tip-pos="below">${SVG_INFO_CIRCLE}</span>`
      html += `<span class="inline-flex items-center gap-1 ${badgeBg} text-[12px] font-medium rounded-lg px-2.5 py-[2px]">${iconHtml} ${signedCount}/${reqSigs} Signed ${tooltipHtml}</span>`
    } else {
      html += `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">DRAFT</span>`
    }

    return html
  }

  _buildFullCardHTML(data) {
    const seq = data.sequence
    const isPast = data.is_past_payment || false
    const pdfUrl = data.final_pdf_url || `/projects/${this.projectIdValue}/finances/co_export_with_pdfs/${seq}.pdf`
    const coTotal = data.co_total || 0
    const cur = this.currencyValue
    const pcoChips = (data.pco_sequences || []).filter(s => s != null).sort((a, b) => a - b)
      .map(s => `<span class="inline-flex items-center gap-1 bg-blue-50 text-blue-600 text-[11px] font-medium rounded px-1.5 py-[1px]">${SVG_LINK} PCO#${s}</span>`)
      .join("")
    const ccdChips = (data.ccd_sequences || []).filter(s => s != null).sort((a, b) => a - b)
      .map(s => `<span class="inline-flex items-center gap-1 bg-blue-50 text-blue-600 text-[11px] font-medium rounded px-1.5 py-[1px]">${SVG_LINK} CCD#${s}</span>`)
      .join("")
    const chipsRow = (pcoChips || ccdChips)
      ? `<span data-chips-area class="flex flex-wrap items-center gap-1.5 min-w-0">${pcoChips}${ccdChips}</span>`
      : ""

    return `<div class="co-card flex flex-col gap-2 px-3 py-2.5 text-[13px] text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" data-sequence="${seq}" data-signed-count="${data.signed_count || 0}" data-is-past="${isPast}" data-action="click->change-order#editCo">
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-3 min-w-0">
          <span class="font-semibold text-gray-700 text-[14px] whitespace-nowrap">CO #${seq}</span>
          <span data-badge-area>${this._buildBadgeHTML(data)}</span>
          ${chipsRow}
        </div>
        <div class="flex items-center gap-3 shrink-0">
          <span data-export-area>
            <div class="relative" data-controller="dropdown">
              <button data-action="click->dropdown#toggle" class="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px] transition-colors cursor-pointer">Export <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg></button>
              <div data-dropdown-target="menu" class="hidden absolute right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-[100px] py-1">
                <a href="${pdfUrl}" target="_blank" data-action="click->change-order#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">PDF</a>
              </div>
            </div>
          </span>
          <span data-last-updated class="hidden sm:inline text-gray-400 text-[12px] italic whitespace-nowrap text-right">Last updated on ${data.last_updated_at || ""} by ${data.who_updated || ""}</span>
          <span data-card-total-amount class="font-semibold text-gray-700 text-[14px] min-w-[110px] text-right whitespace-nowrap">${cur}${this._formatAmount(coTotal)}</span>
        </div>
      </div>
    </div>`
  }

  _updatePageCard(data) {
    const card = this.element.querySelector(`.co-card[data-sequence="${data.sequence}"]`)
    if (!card) return
    card.outerHTML = this._buildFullCardHTML(data)
  }

  _insertNewPageCard(data) {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    const cardsContainer = this.hasCoCardsTarget ? this.coCardsTarget : null
    if (!cardsContainer) return
    cardsContainer.classList.add("flex", "flex-col", "gap-2")

    const newCardHTML = this._buildFullCardHTML(data)
    const newSeq = data.sequence
    const existingCards = cardsContainer.querySelectorAll(".co-card[data-sequence]")
    let inserted = false
    for (const card of existingCards) {
      const cardSeq = parseInt(card.dataset.sequence)
      if (newSeq > cardSeq) {
        card.insertAdjacentHTML("beforebegin", newCardHTML)
        inserted = true
        break
      }
    }
    if (!inserted) {
      cardsContainer.insertAdjacentHTML("beforeend", newCardHTML)
    }
  }

  _removePageCard(sequence) {
    const card = this.element.querySelector(`.co-card[data-sequence="${sequence}"]`)
    if (card) card.remove()
  }

  _hasPreviousUnsignedCos(currentSequence) {
    const cards = this.element.querySelectorAll(".co-card")
    for (const card of cards) {
      const seq = parseInt(card.dataset.sequence)
      if (seq >= currentSequence) continue
      if (card.dataset.isPast === "true") continue
      const signedCount = parseInt(card.dataset.signedCount) || 0
      if (signedCount < this._requiredSigs) return true
    }
    return false
  }

  // -- Modal --

  hideModal() {
    this.newCoModalTarget.classList.add("hidden")
    this._attachmentIds = []
    this._pendingUploads = 0
    if (this.hasCoFileListTarget) this.coFileListTarget.innerHTML = ""
    showIntercom()
  }

  stopPropagation(e) {
    e.stopPropagation()
  }

  // -- PDF polling --

  _startPdfPolling(sequence, docType) {
    if (this._pdfPollingTimers[sequence]) clearTimeout(this._pdfPollingTimers[sequence])
    this._setExportGenerating(sequence)

    const triggeredAt = Math.floor(Date.now() / 1000)
    const maxPollTime = 90000
    const poll = async () => {
      if (Date.now() - triggeredAt * 1000 > maxPollTime) {
        delete this._pdfPollingTimers[sequence]
        this._restoreExportButton(sequence, null)
        return
      }
      const data = await this._ajax("POST", "check_if_final_pdf_is_generating", { type: docType, sequence: sequence, triggered_at: triggeredAt })
      if (data && !data.generating) {
        delete this._pdfPollingTimers[sequence]
        this._restoreExportButton(sequence, data.pdf_url)
      } else {
        this._pdfPollingTimers[sequence] = setTimeout(poll, 3000)
      }
    }
    this._pdfPollingTimers[sequence] = setTimeout(poll, 3000)
  }

  _setExportGenerating(sequence) {
    const card = this.element.querySelector(`.co-card[data-sequence="${sequence}"]`)
    if (!card) return
    const area = card.querySelector("[data-export-area]")
    if (!area) return
    area.innerHTML = `<span class="inline-flex items-center gap-1.5 text-amber-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]"><svg class="w-3 h-3 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Generating PDF...</span>`
  }

  _restoreExportButton(sequence, pdfUrl) {
    const card = this.element.querySelector(`.co-card[data-sequence="${sequence}"]`)
    if (!card) return
    const area = card.querySelector("[data-export-area]")
    if (!area) return
    const url = pdfUrl || `/projects/${this.projectIdValue}/finances/co_export_with_pdfs/${sequence}.pdf`
    area.innerHTML = `<div class="relative" data-controller="dropdown">
      <button data-action="click->dropdown#toggle" class="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px] transition-colors cursor-pointer">Export <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg></button>
      <div data-dropdown-target="menu" class="hidden absolute right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-[100px] py-1">
        <a href="${url}" target="_blank" data-action="click->change-order#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">PDF</a>
      </div>
    </div>`
  }

  // -- Toast --

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.style.zIndex = "9999"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // -- AJAX helper --

  async _ajax(method, action, params = {}) {
    const url = `/projects/${this.projectIdValue}/finances/${action}`
    const body = new FormData()
    Object.entries(params).forEach(([k, v]) => body.append(k, v))

    const options = {
      method: method === "GET" ? "GET" : "POST",
      headers: { "X-CSRF-Token": this._csrfToken },
    }

    if (method !== "GET") {
      if (method !== "POST") body.append("_method", method.toLowerCase())
      options.body = body
    }

    try {
      const resp = await fetch(url, options)
      if (resp.headers.get("content-type")?.includes("application/json")) {
        return await resp.json()
      }
      return resp.ok
    } catch (e) {
      console.error("CO request failed:", e)
    }
  }
}
